import { useModelLabel } from "@judo/i18n";
import type { BinaryTypeInput, Input } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { useState } from "react";

export interface BinaryInputComponentProps {
	element: BinaryTypeInput | Input;
	readOnly?: boolean;
	value?: string | null; // Base64 encoded or URL
	onChange?: (value: string | null) => void;
	error?: string;
	disabled?: boolean;
	fileName?: string;
}

/**
 * Binary/file input component.
 */
export function BinaryInputComponent({
	element,
	readOnly,
	value: _value,
	onChange,
	error,
	disabled,
	fileName,
}: BinaryInputComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const [currentFileName, setCurrentFileName] = useState(fileName ?? "");

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setCurrentFileName(file.name);
			const reader = new FileReader();
			reader.addEventListener("load", () => {
				const base64 = (reader.result as string).split(",")[1];
				onChange?.(base64);
			});
			reader.readAsDataURL(file);
		}
	};

	const handleClear = () => {
		setCurrentFileName("");
		onChange?.(null);
	};

	const isDisabled = disabled ?? element.disabled;
	const isReadOnly = readOnly ?? element.isReadOnly;

	return (
		<Box data-testid={testId}>
			<Typography variant="body2" gutterBottom>
				{label}
				{element.required && " *"}
			</Typography>
			<Box display="flex" alignItems="center" gap={1}>
				<Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={isDisabled || isReadOnly}>
					Upload
					<input type="file" hidden onChange={handleFileSelect} data-testid={`${testId}::file-input`} />
				</Button>
				{currentFileName && (
					<>
						<Typography variant="body2" sx={{ flexGrow: 1 }}>
							{currentFileName}
						</Typography>
						{!isReadOnly && (
							<IconButton onClick={handleClear} disabled={isDisabled} size="small" data-testid={`${testId}::clear`}>
								<DeleteIcon />
							</IconButton>
						)}
					</>
				)}
			</Box>
			{error && (
				<Typography variant="caption" color="error">
					{error}
				</Typography>
			)}
		</Box>
	);
}
